import moment from 'moment'

/** US format expected by SpendSail API (invariant on server). */
export const API_DATE_FORMAT = 'MM/DD/YYYY'

const PARSE_FORMATS = [
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MM-DD-YYYY',
  'M-D-YYYY',
  'DD/MM/YYYY',
  'D/M/YYYY',
  'DD-MM-YYYY',
  'D-M-YYYY',
  'YYYY-MM-DD',
  'YYYY/MM/DD',
]

let cachedLocaleFormat: string | null = null

/** Detect the browser/laptop short date pattern (e.g. DD/MM/YYYY vs MM/DD/YYYY). */
export function getLocaleDateFormat(): string {
  if (cachedLocaleFormat) return cachedLocaleFormat

  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    }).formatToParts(new Date(2006, 10, 22))

    const order = parts
      .filter((p) => p.type === 'day' || p.type === 'month' || p.type === 'year')
      .map((p) => p.type)

    const sep =
      parts.find((p) => p.type === 'literal' && (p.value === '/' || p.value === '-' || p.value === '.'))
        ?.value ?? '/'

    if (order[0] === 'day') {
      cachedLocaleFormat = `DD${sep}MM${sep}YYYY`
    } else if (order[0] === 'year') {
      cachedLocaleFormat = `YYYY${sep}MM${sep}DD`
    } else {
      cachedLocaleFormat = `MM${sep}DD${sep}YYYY`
    }
  } catch {
    cachedLocaleFormat = API_DATE_FORMAT
  }

  return cachedLocaleFormat
}

export function parseFlexibleDate(
  value: string | moment.Moment | Date | null | undefined
): moment.Moment | null {
  if (value == null || value === '') return null
  if (moment.isMoment(value)) return value.isValid() ? value : null
  if (value instanceof Date) {
    const m = moment(value)
    return m.isValid() ? m : null
  }

  const trimmed = String(value).trim()
  if (!trimmed) return null

  const localeFormat = getLocaleDateFormat()
  // Prefer API US format before locale so stored filter values (MM/DD/YYYY)
  // are not misread as DD/MM on day-first locales (e.g. 09/02 → Sep 2, not Feb 9).
  for (const fmt of [API_DATE_FORMAT, 'MM-DD-YYYY', localeFormat, ...PARSE_FORMATS]) {
    const m = moment(trimmed, fmt, true)
    if (m.isValid()) return m
  }

  const loose = moment(trimmed)
  return loose.isValid() ? loose : null
}

/** Always send US format to API regardless of laptop locale. */
export function formatDateForApi(value: string | moment.Moment | Date | null | undefined): string {
  const m = parseFlexibleDate(value)
  return m ? m.format(API_DATE_FORMAT) : ''
}

/** Format for UI display using laptop locale pattern. */
export function formatDateForDisplay(value: string | moment.Moment | Date | null | undefined): string {
  const m = parseFlexibleDate(value)
  return m ? m.format(getLocaleDateFormat()) : ''
}

export function todayForDisplay(): string {
  return moment().format(getLocaleDateFormat())
}

export function daysAgoForDisplay(days: number): string {
  return moment().subtract(days, 'days').format(getLocaleDateFormat())
}

export function formatDateRangeDisplay(start: string, end: string): string {
  return `${formatDateForDisplay(start)} - ${formatDateForDisplay(end)}`
}

export const API_DATE_QUERY_PARAMS = new Set([
  'startDate',
  'endDate',
  'dateFrom',
  'dateTo',
  'itemCreatedStartDate',
  'itemCreatedRangeEndDate',
  'receiptStartDate',
  'receiptEndDate',
  'itemStartDate',
  'itemEndDate',
])

export function normalizeDateQueryParams(params: Record<string, unknown>): Record<string, unknown> {
  const next = { ...params }
  Object.keys(next).forEach((key) => {
    if (!API_DATE_QUERY_PARAMS.has(key)) return
    const raw = next[key]
    if (raw == null || raw === '') return
    const apiValue = formatDateForApi(String(raw))
    if (apiValue) next[key] = apiValue
  })
  return next
}

export function normalizeDateQueryString(url: string): string {
  const qIdx = url.indexOf('?')
  if (qIdx < 0) return url

  const base = url.slice(0, qIdx)
  const params = new URLSearchParams(url.slice(qIdx + 1))

  params.forEach((value, key) => {
    if (!API_DATE_QUERY_PARAMS.has(key) || !value) return
    const apiValue = formatDateForApi(value)
    if (apiValue) params.set(key, apiValue)
  })

  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}
